import { Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { Tag } from 'src/app/people/models/tag';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseService } from 'src/app/shared/services/base.service';
import { PersonDetailsService } from './person-details.service';
import { ApiVersions } from './api-version';

@Injectable()
export class TagService extends BaseService {
  private _tags: BehaviorSubject<Tag[]> = new BehaviorSubject<Tag[]>([]);

  public readonly tags: Observable<Tag[]> = this._tags.asObservable();

  constructor(personDetailsService: PersonDetailsService) {
    super();

    personDetailsService.personUpdated.subscribe(() => {
      this.getTags();
    });
  }

  getTags(): Observable<Tag[]> {
    let obs = this.get(this.tagsBaseUrl()).pipe(map(this.convertTagsData));
    obs.subscribe((tags) => {
      this._tags.next(tags);
    });

    return obs;
  }

  deleteTag(tagId: string): Promise<boolean> {
    return lastValueFrom(
      this.delete(`${this.tagsBaseUrl()}/${tagId}`, undefined, ApiVersions.v0_1, true).pipe(
        map((result) => {
          if (result) {
            this._tags.next(this._tags.getValue().filter((t) => t.id !== tagId));
            return true;
          }
          return false;
        })
      )
    );
  }

  createTag(tag: Tag): Promise<Tag> {
    let tagStream = JSON.stringify(tag);
    return lastValueFrom(this.post(this.tagsBaseUrl(), tagStream)).then((tag) => {
      const newTag = new Tag(tag);
      this._tags.next([...this._tags.getValue(), newTag]);
      return newTag;
    });
  }

  updateTag(tag: Tag) {
    let tagStream = JSON.stringify(tag);
    return this.put(this.tagsBaseUrl().concat(`/${tag.id}`), tagStream).pipe(
      map((t) => {
        this._tags.next(
          this._tags
            .getValue()
            .map((existingTag) => (existingTag.id === t.id ? new Tag(t) : existingTag))
        );
        return new Tag(t);
      })
    );
  }

  private tagsBaseUrl() {
    return 'tags';
  }

  private convertTagsData = (tags) => (tags ?? []).map((tag) => new Tag(tag));
}
